<template>
    <v-navigation-drawer
        v-model="drawer"
        class="order-navigation-drawer no-transition"
        :mobile-breakpoint="960"
        width="300px"
        dark
        app
    >
        <div v-if="render" class="order-items">
            <div class="header">
                <v-menu
                    class="ma-0"
                    bottom
                    transition="slide-y-transition"
                >
                    <template v-slot:activator="{ on }">
                        <v-btn
                            class="mx-2"
                            v-on="on"
                            icon
                        >
                            <v-icon>far fa-ellipsis-v</v-icon>
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item
                            :key="1"
                            @click="showSelectProducts"
                        >
                            <v-list-item-action class="mr-3">
                                <v-icon>fal fa-plus</v-icon>
                            </v-list-item-action>
                            <v-list-item-title>{{ $t('order.add_products') }}</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            :key="2"
                            :disabled="splitReceipt.active"
                            @click="setSeparator()"
                        >
                            <v-list-item-action class="mr-3">
                                <v-icon :color="curOrder && curOrder.getItemSeparators().includes('' + curOrder.items.length) ? '#1565C0' : (splitReceipt.active ? '#aaaaaa' : '#757575')">
                                    fal fa-concierge-bell
                                </v-icon>
                            </v-list-item-action>
                            <v-list-item-title>
                                <div :style="{'color': curOrder && curOrder.getItemSeparators().includes('' + curOrder.items.length) ? '#1565C0' : (splitReceipt.active ? '#aaaaaa' : '#333333')}">
                                    {{ $t('order.breakpoint') }}
                                </div>
                            </v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            v-if="locale !== 'me'"
                            :key="3"
                            :disabled="disableSplitReceipt"
                            @click="toggleSplitReceipt()"
                        >
                            <v-list-item-action class="mr-3">
                                <v-icon :color="splitReceipt.active ? '#1565C0' : (disableSplitReceipt ? '#aaaaaa' : '#757575')">
                                    fal fa-cut
                                </v-icon>
                            </v-list-item-action>
                            <v-list-item-title>
                                <div :style="{'color': splitReceipt.active ? '#1565C0' : (disableSplitReceipt ? '#aaaaaa' : '#333333')}">
                                    {{ $t('order.split_receipt') }}
                                </div>
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-tabs
                    v-model="curGuest"
                    class="tabs"
                    background-color="transparent"
                    :slider-color="activeTabs[curGuest] ? 'accent' : 'white'"
                    fixed-tabs
                >
                    <v-tab
                        v-for="guestId in [0, 1, 2, 3]"
                        v-show="!splitReceipt.active || curGuest == guestId"
                        :key="guestId"
                        :class="{ 'first-tab': guestId === 0, 'active': activeTabs[guestId] }"
                        :disabled="!!(curGuest !== guestId && curOrder && curOrder.items.length)"
                    >
                        G{{ guestId + 1 }}
                    </v-tab>
                </v-tabs>

                <v-menu
                    v-if="splitReceipt.active"
                    transition="slide-y-transition"
                    bottom
                    right
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            class="mx-4"
                            color="warning"
                            rounded
                            small
                            v-bind="attrs"
                            v-on="on"
                            :disabled="selectedItemsEmpty"
                            @click.stop.prevent
                        >
                            {{ $t('order.transfer_to') }}
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item
                            v-for="guestId in [0,1,2,3].filter(g => g !== curGuest)"
                            :key="guestId"
                            @click="transferToGuest(guestId)"
                        >
                            <v-list-item-title>G{{ guestId + 1 }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div
                ref="content"
                v-resize="onResize"
                class="content"
            >
                <v-virtual-scroll
                    ref="virtualScroll"
                    class="virtual-scroll"
                    :items="orderItems"
                    :item-height="55"
                    :bench="3"
                    :height="virtualScrollHeight"
                >
                    <template v-slot="{ item, index }">
                        <div
                            v-if="item"
                            v-ripple
                            class="item"
                            :class="{
                                selected: selectedItem && selectedItems.hasOwnProperty(item.hash),
                                highlight: item.highlight,
                                separator: item.separator,
                                selectable: splitReceipt.active && !selectedItems.hasOwnProperty(item.hash),
                                selected: splitReceipt.active && selectedItems.hasOwnProperty(item.hash)
                            }"
                        >
                            <div
                                class="item-count"
                                @click.stop.prevent="onItemClick(item)"
                            >
                                {{ item.getCount() + ' x ' }}
                            </div>

                            <div
                                class="item-label"
                                :class="{ discount: !item.getDiscountAmount().isZero() }"
                                @click.stop.prevent="onItemClick(item)"
                            >
                                <div class="item-name">
                                    {{ item.name }}
                                </div>
                                <div
                                    v-if="!item.getDiscountAmount().isZero()"
                                    class="item-discount"
                                >
                                    <div class="item-discount-label">
                                        {{ `${$t('general.discount')} ${item.getDiscountPercent()}%` }}
                                    </div>
                                </div>
                            </div>

                            <v-icon
                                v-if="item.comments.length"
                                class="mx-2"
                                color="warning"
                                size="14"
                            >
                                fal fa-comment-lines
                            </v-icon>

                            <div
                                v-if="!selectedItems.hasOwnProperty(item.hash)"
                                class="item-price"
                                @click.stop.prevent="onItemClick(item)"
                            >
                                {{ item.getPrice().toFormat('0.00') }}
                            </div>

                            <div
                                v-if="selectedItems.hasOwnProperty(item.hash)"
                                class="item-quantity-select"
                            >
                                <v-btn
                                    class="mr-1"
                                    icon
                                    small
                                    :disabled="selectedItems[item.hash] <= 0"
                                    @click.stop.prevent="addToSelectedQuantity(item, -1)"
                                >
                                    <v-icon>fal fa-minus</v-icon>
                                </v-btn>

                                <number-field
                                    ref="selectQuantityField"
                                    v-model="selectedItems[item.hash]"
                                    class="select-quantity-field"
                                    :precision="3"
                                    :max="item.getCount()"
                                    :min="0"
                                    unsigned
                                    dense
                                    filled
                                    hide-details
                                    @click="$refs.selectQuantityField.focus(); $refs.selectQuantityField.selectAll()"
                                />

                                <v-btn
                                    class="ml-1"
                                    icon
                                    small
                                    :disabled="selectedItems[item.hash] >= item.getCount()"
                                    @click.stop.prevent="addToSelectedQuantity(item, 1)"
                                >
                                    <v-icon>fal fa-plus</v-icon>
                                </v-btn>
                            </div>

                            <div
                                v-show="!selectedItems.hasOwnProperty(item.hash)"
                                class="item-menu"
                            >
                                <v-menu
                                    v-if="(locale !== 'me' || isNewItem(item)) && !splitReceipt.active"
                                    v-model="showMenu[item.hash]"
                                    class="ma-0"
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            v-on="on"
                                            icon
                                            small
                                            @click.stop.prevent="onItemClick(item)"
                                        >
                                            <v-icon>far fa-ellipsis-v</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            v-if="isNewItem(item)"
                                            :key="1"
                                            @click="showDialog(item, 'quantity')"
                                        >
                                            <v-list-item-action class="mr-3">
                                                <v-icon>fal fa-sort-numeric-up</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.quantity') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item
                                            v-if="locale !== 'me' || isNewItem(item)"
                                            :key="2"
                                            :disabled="!allows('allow_item_discount')"
                                            @click="showDialog(item, 'discount')"
                                        >
                                            <v-list-item-action class="mr-3">
                                                <v-icon :disabled="!allows('allow_item_discount')">fal fa-percent</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.discount') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item
                                            v-if="isNewItem(item)"
                                            :key="3"
                                            @click="showDialog(item, 'comments')"
                                        >
                                            <v-list-item-action class="mr-3">
                                                <v-icon>fal fa-comment</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.comment') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item
                                            v-if="locale !== 'me' || isNewItem(item)"
                                            :key="4"
                                            class="cancel-item"
                                            :disabled="!allows('allow_cancel_item') && !isNewItem(item)"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-3">
                                                <v-icon>fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>
                                                {{ $t('general.delete_item') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </div>
                    </template>
                </v-virtual-scroll>
            </div>

            <div class="footer">
                <div class="summary">
                    <div class="summary-item buyer-item">
                        <div class="mr-2">{{ $tc('general.buyer', 1) }}</div>
                        <div>{{ client ? client.name : $t('general.not_selected') }}</div>
                    </div>
                    <div class="summary-item">
                        <span>{{ $t('general.sum') }}</span>
                        <strong>{{ summary.price.toFormat('0.00') }}</strong>
                    </div>
                    <div class="summary-item">
                        <span>{{ $t('general.discount') }}</span>
                        <strong>{{ summary.discount.toFormat('0.00') }}</strong>
                    </div>
                    <div v-if="curOrder && curOrder.getPaidAdvance().toUnit() > 0" class="summary-item">
                        <span>{{ $t('general.advance') }}</span>
                        <div>
                            <span v-if="curOrder.getPaidAdvanceEffective().toUnit() != curOrder.getPaidAdvance().toUnit()">
                                {{ curOrder.getPaidAdvance().toFormat('0.00') }} |
                            </span>
                            <span class="advance">{{ curOrder.getPaidAdvanceEffective().toFormat('0.00') }}</span>
                        </div>
                    </div>
                    <div class="summary-item">
                        <span>{{ $t('general.to_pay') }}</span>
                        <strong class="to-pay">{{ summary.toPay.toFormat('0.00') }}</strong>
                    </div>
                </div>
            </div>

            <!-- Select Order Item Comments Dialog -->
            <select-order-item-comments
                :show.sync="show.comments"
                :order-item="selectedItem"
                @select="selectItemComments"
            />

            <!-- Discount Window -->
            <numpad-expanded
                :show.sync="show.discount"
                type="discount"
                :title="$t('general.discount_item')"
                :value="selectedItem ? selectedItem.getDiscountPercent() : 0"
                :total="selectedItem ? selectedItem.getPrice().toRoundedUnit(2) : 0"
                :max="99"
                @confirm="giveItemDiscount(selectedItem, $event.value)"
            />

            <!-- Quantity Window -->
            <numpad-expanded
                :show.sync="show.quantity"
                type="count"
                :title="$t('general.quantity')"
                :value="selectedItem ? selectedItem.getCount() : 0"
                @confirm="updateItemCount(selectedItem, $event.value)"
            />
        </div>
    </v-navigation-drawer>
</template>

<script>
import NumpadExpanded from '@/components/NumpadExpanded';
import SelectOrderItemComments from '@/views/aria/order/SelectOrderItemComments';
import NumberField from '@/components/NumberField';
import { mapGetters } from 'vuex';
import * as mutationTypes from '@/store/mutationTypes';

export default {
    name: 'OrderNavigationDrawer',

    components: {
        NumpadExpanded,
        SelectOrderItemComments,
        NumberField
    },

    created() {
        this.drawer = this.$vuetify.breakpoint.mdAndUp;

        this.eventBus.$on('order-toggle-drawer', this.toggleDrawer);
        this.eventBus.$on('order-select-item', this.onItemSelect);
        this.eventBus.$on('order-split-receipt-reset', this.splitReceiptReset);

        this.$store.commit(mutationTypes.SET_CUR_GUEST, { curGuest: this.curGuest });
        this.$store.commit(mutationTypes.SET_SPLIT_RECEIPT, { splitReceipt: this.splitReceipt });
    },

    mounted() {
        // activated
        this.onResize()
    },

    beforeDestroy() {
        this.eventBus.$off('order-toggle-drawer', this.toggleDrawer);
        this.eventBus.$off('order-select-item', this.onItemSelect)
        this.eventBus.$off('order-split-receipt-reset', this.splitReceiptReset);
    },

    data: () => ({
        curGuest: 0,
        drawer: false,
        render: false,

        selectedItem: null,

        show: {
            discount: false,
            comments: false,
            quantity: false
        },

        showMenu: {},

        virtualScrollHeight: 500,
        splitReceipt: { active: false, data: [] },
        selectedItems: {}
    }),

    watch: {
        curOrder: {
            deep: true,
            handler(val) {
                if(!val) {
                    this.showMenu = {};
                    return
                }

                let showMenu = {};
                val.items.forEach(item => {
                    showMenu[item.hash] = false;
                });

                this.showMenu = showMenu;
            }
        },

        curGuest(val) {
            this.$store.commit(mutationTypes.SET_CUR_GUEST, { curGuest: val });
        },

        splitReceipt: {
            deep: true,
            handler(val) {
                this.$store.commit(mutationTypes.SET_SPLIT_RECEIPT, { splitReceipt: val });
            }
        },

        selectedItems: {
            deep: true,
            handler(val) {
                if(!this.splitReceipt.active) return;

                const splitReceiptData = [];
                Object.keys(val).filter(hash => val[hash]).forEach(hash => {
                    const item = this.unpaidOrders.flatMap(order => order.items).find(item => item.hash === hash)
                    const existingRecord = splitReceiptData.find(so => so.order.hash === item.order_hash)
                    if(existingRecord) {
                        existingRecord.items.push({ item, count: val[hash] })
                    } else {
                        const order = this.unpaidOrders.find(order => order.hash === item.order_hash)
                        splitReceiptData.push({ order, items: [{ item, count: val[hash] }] })
                    }
                })

                this.splitReceipt.data = splitReceiptData
            }
        },

        drawer(val) {
            this.eventBus.$emit('order-drawer', val)

            if(val) {
                this.render = true
            } else {
                setTimeout(() => (this.render = false), 200)
            }
        }
    },

    computed: {
        ...mapGetters(['curOrder', 'unpaidOrders', 'curUnpaidOrders']),

        orderItems() {
            let orderItems = []

            this.unpaidOrders.forEach((order, index) => {
                const highlight = (this.unpaidOrders.length + index) % 2 === 0
                const items = order.$duplicate().items.map(item => {
                    item.order = order
                    item.highlight = highlight
                    return item
                })

                orderItems = [...orderItems, ...items]
            })

            const addCurOrder = !this.splitReceipt.active && this.curOrder && (this.curOrder.getGuestId() || 0) === this.curGuest;
            if(addCurOrder) {
                const curOrderItems = this.curOrder.$duplicate().items.map((item, index) => {
                    item.order = this.curOrder
                    item.highlight = true
                    item.separator = this.curOrder.getItemSeparators().includes('' + index);
                    return item
                })
                orderItems = [...orderItems, ...curOrderItems]
            }

            return orderItems.reverse()
        },

        selectedItemsEmpty() {
            return Object.keys(this.selectedItems).filter(hash => !!this.selectedItems[hash]).length === 0
        },

        summary() {
            if(this.splitReceipt.active) {
                return {
                    price: this.curOrder.getPrice(),
                    discount: this.curOrder.getDiscountAmount(),
                    toPay: this.curOrder.getLeftToPay()
                }
            }

            let price = Money.parse(0)
            let discount = Money.parse(0)
            let toPay = Money.parse(0)

            this.unpaidOrders.forEach(order => {
                price = price.add(order.getPrice())
                discount = discount.add(order.getDiscountAmount())
                toPay = toPay.add(order.getToPay())
            })

            if(this.curOrder) {
                price = price.add(this.curOrder.getPrice())
                discount = discount.add(this.curOrder.getDiscountAmount())
                toPay = toPay.add(this.curOrder.getLeftToPay())
            }

            return {
                price: price,
                discount: discount,
                toPay: toPay,
            }
        },

        client() {
            let client = this.curOrder?.getClient();
            if(client) return client;

            const unpaidOrderWithClient = this.unpaidOrders.find(order => !!order.client_id);
            return unpaidOrderWithClient?.getClient() || null;
        },

        activeTabs() {
            return [
                this.curUnpaidOrders.some(o => !o.getGuestId()),
                this.curUnpaidOrders.some(o => o.getGuestId() === 1),
                this.curUnpaidOrders.some(o => o.getGuestId() === 2),
                this.curUnpaidOrders.some(o => o.getGuestId() === 3)
            ]
        },

        disableSplitReceipt() {
            return !this.curOrder || (!this.splitReceipt.active && this.curOrder.items.length > 0)
        }
    },

    methods: {
        toggleDrawer() {
            this.drawer = !this.drawer;
        },

        showDialog(item, dialog) {
            if (dialog === 'discount' && !this.allows('allow_item_discount')) {
                return;
            }

            this.show[dialog] = true;
            this.selectedItem = item;
        },

        onItemSelect(selectedItem) {
            if(!selectedItem) return;

            let scrollToItem = true;
            if (this.selectedItem && selectedItem.hash === this.selectedItem.hash) {
                scrollToItem = false;
            }

            this.selectedItem = selectedItem;

            if(!scrollToItem) {
                return;
            }

            setTimeout(() => {
                if(this.$refs.virtualScroll) {
                    this.$refs.virtualScroll.$el.scrollTop = 0;
                }
            }, 150);
        },

        isNewItem(item) {
            return this.curOrder && this.curOrder.items.some(i => i.hash === item.hash)
        },

        onItemClick(item) {
            if(locale === 'me' && !this.isNewItem(item)) return

            if(this.splitReceipt.active) {
                if(this.selectedItems[item.hash] !== undefined) {
                    this.$delete(this.selectedItems, item.hash);
                } else {
                    this.$set(this.selectedItems, item.hash, parseFloat(item.count));
                }
                return;
            }

            this.selectedItem = item;

            Object.keys(this.showMenu).forEach(hash => {
                if (hash !== item.hash) {
                    this.$set(this.showMenu, hash, false);
                }
            });

            this.$set(this.showMenu, item.hash, !this.showMenu[item.hash]);
        },

        addToSelectedQuantity(item, quantity) {
            let newQuantity = (this.selectedItems[item.hash] || 0) + quantity;

            if(newQuantity < 0) newQuantity = 0;
            if(newQuantity > item.getCount()) newQuantity = item.getCount();

            if(newQuantity === 0) {
                this.$delete(this.selectedItems, item.hash);
                return;
            }

            this.$set(this.selectedItems, item.hash, newQuantity);
        },

        deleteItem(orderItem) {
            if (!this.allows('allow_cancel_item') && !this.isNewItem(orderItem)) {
                return;
            }

            this.eventBus.$emit('order-delete-item', { item: orderItem, order: orderItem.order });
            this.selectedItem = null;
        },

        selectItemComments(orderItem, comments) {
            this.eventBus.$emit('order-select-item-comments', { item: orderItem, comments: comments });
        },

        updateItemCount(orderItem, count) {
            this.eventBus.$emit('order-count-update', { item: orderItem, count: count })
        },

        giveItemDiscount(orderItem, percent) {
            if (!this.allows('allow_item_discount')) {
                return;
            }

            this.eventBus.$emit('order-discount', { item: orderItem, order: orderItem.order, percent: percent })
        },

        setSeparator() {
            this.eventBus.$emit('order-set-item-separator')
        },

        showSelectProducts() {
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'selectProducts' });
        },

        toggleSplitReceipt() {
            const splitReceiptActive = this.splitReceipt.active
            if(!this.curOrder || (!splitReceiptActive && this.curOrder.items.length > 0)) return;

            this.splitReceiptReset();
            this.splitReceipt.active = !splitReceiptActive;
        },

        transferToGuest(guestId) {
            this.eventBus.$emit('order-transfer-to-guest', guestId);
        },

        splitReceiptReset() {
            this.selectedItems = {};
            this.splitReceipt = { active: false, data: [] };
        },

        onResize() {
            setTimeout(() => {
                if(this.$refs.content && this.$refs.content.offsetHeight > 0) {
                    this.virtualScrollHeight = this.$refs.content.offsetHeight;
                }
            }, 250)
        },
    }
};
</script>

<style lang="scss" scoped>
.order-navigation-drawer {
    .order-items {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow-x: hidden;
        overflow-y: hidden;
        font-size: 14px;
        background: var(--v-order_sidebar_bg-base);
        color: var(--v-order_sidebar_text-base);

        .header {
            font-size: 16px;
            background: var(--v-order_sidebar_header_bg-base);
            font-weight: bold;
            display: flex;
            align-items: center;
            height: 48px;
            width: 300px;

            .tabs {
                height: 100%;

                ::v-deep .v-item-group {
                    height: 100%;

                    .v-tab {
                        min-width: 60px;
                        max-width: 66px;
                        &.first-tab {
                            margin-left: 0px !important;
                        }
                        &.active {
                            color: var(--v-accent-base) !important;
                        }
                    }

                    .v-slide-group__prev {
                        display: none !important;
                    }
                }
            }
        }

        .content {
            flex: 1;
            padding: 0;
            border-bottom: 1px solid var(--v-order_sidebar_border-base);
            overflow-y: hidden;
            margin-bottom: 140px;
            border-radius: 0;

            .virtual-scroll {
                overflow-x: hidden;

                .v-list-item {
                    padding: 0;
                }
            }
        }

        .footer {
            min-height: 140px;
            position: fixed;
            bottom: 0;
            width: 100%;

            .summary {
                background: var(--v-order_sidebar_footer_bg-base);

                .summary-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 10px;
                    min-height: 35px;
                    border-bottom: 1px solid var(--v-order_sidebar_border-base);
                    text-align: right;

                    .to-pay {
                        color: var(--v-warning-base);
                    }

                    &:last-child {
                        border: none;
                    }

                    .advance {
                        color: var(--v-error-base);
                    }
                    &.buyer-item {
                        border-width: 1px 0;
                        border-style: solid;
                        border-color: #515364;
                    }
                }
            }
        }

        .selected {
            background: var(--v-order_sidebar_selected_bg-base);
            color: var(--v-order_sidebar_selected_text-base) !important;
        }

        .highlight {
            background: var(--v-order_sidebar_highlight_bg-base);
            color: var(--v-order_sidebar_highlight_text-base);
        }

        .separator {
            border-bottom: 1px dashed #318cf5;
        }

        .selectable {
            opacity: 0.8;
        }

        .selected {
            background: #1d1f27;
            border-left: 2px solid var(--v-order_sidebar_selected_text-base);
        }

        .item {
            height: 55px;
            display: flex;
            cursor: pointer;

            .item-count {
                margin-left: 6px;
                display: flex;
                align-items: center;
            }

            .item-label {
                padding: 0 8px;
                height: 55px;
                flex: 1;

                &.discount {
                    height: 32px;
                }

                .item-name {
                    display: flex;
                    align-items: center;
                    height: 100%;
                    word-break: break-word;
                    flex: 1;
                    line-height: 1.1;
                }

                .item-discount {
                    display: flex;
                    align-items: flex-start;
                    height: 23px;
                    padding: 2px 0;

                    .item-discount-label {
                        border-left: 2px solid #318cf5;
                        padding-left: 6px;
                        height: 17px;
                        font-size: 13px;
                        color: rgb(177, 177, 177);
                    }
                }
            }

            .item-price {
                display: flex;
                align-items: center;
            }

            .item-quantity-select {
                display: flex;
                align-items: center;
                margin-right: 4px;
                .select-quantity-field {
                    max-width: 40px;
                    text-align: center;
                    font-size: 14px;

                    ::v-deep .v-input__slot {
                        padding: 0;
                        input {
                            text-align: center;
                        }
                    }
                }
            }

            .item-menu {
                width: 30px;
                display: flex;
                align-items: center;
                justify-content: flex-end;

                .v-btn {
                    color: #a7b1bb;
                    margin: 0;
                }
            }
        }
    }
}
</style>

<style lang="scss">
.v-list-item {
    &.cancel-item {
        color: red !important;

        i {
            color: red !important;
        }
    }

    &.v-list-item--disabled {
        &.cancel-item {
            opacity: 0.4;
        }
    }
}
</style>
